import { useTranslation } from 'react-i18next';
import { useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { matchPath, useHistory, useRouteMatch } from 'react-router';
import { Link, Spinner } from '../../components/ui';
import cx from 'classnames';
import ContentInfoComments from '../content-details/content-info/content-info-comments';
import useContentFetcher from '../../common/use-content-fetcher';

import './comments-webview.scss';
import { programSelectors } from '../../models/program';
import { uiOperations } from '../../models/ui';
import {
  RootPatronState,
  usePatronSelector,
} from '../../common/use-patron-selector';
import { usePrependProgramPath } from '../../common/use-prepend-program-path';
import { Content } from '../../models/content/types';
import { EXIT_DEEPLINK, getDeeplinkUrl } from '../../lib/mobile-deeplinks/es';
import { Feature, getFeatureFlag } from '../../models/features/features';
import ContentComments from '../../components/v2/comments/content-comments';
import { TextWithMentions } from '../../components/ui/mention-text';
import { flattenMentions } from '../../common/use-plaintext-mentions';

export function CommentsWebviewView() {
  const { params } = useRouteMatch<{ id: string; feedItemId: string }>();
  return (
    <CommentsWebView
      contentId={Number(params.id)}
      feedItemId={params.feedItemId}
    />
  );
}

type CommentsWebViewProps = { contentId: Content['id']; feedItemId: string };

const CommentsWebView = ({
  contentId: id,
  feedItemId,
}: CommentsWebViewProps) => {
  const contentIdParam = Number(id);

  const programId = usePatronSelector((state) =>
    programSelectors.getProgramId(state)
  );
  const orgSlug = usePatronSelector((state) =>
    programSelectors.getProgramOrgSlug(state)
  );

  const { content, fetching } = useContentFetcher({
    contentId: contentIdParam,
    feedItemId,
    isApproval: false,
    // FE-38038: without this, pony returns plain `@Name` and mentions in the content
    // title/description render unlinked.
    mentionFormat: 'with_metadata',
  });

  const history = useHistory();
  const dispatch = useDispatch();
  const contentId = content?.id ?? contentIdParam;

  dispatch(uiOperations.hideHeader());

  useEffect(() => {
    // Navigating out of this component should close the website in all cases
    const unblock = history.block((location) => {
      const webviewPathMatch = matchPath(location.pathname, {
        path: '/webview/contents/:id/comments/:commentId',
      });
      if (webviewPathMatch) {
        return;
      }

      const commentPathMatch = matchPath(location.pathname, {
        path: '/contents/:id/comments/:commentId',
      });
      if (commentPathMatch) {
        location.pathname = `/webview${location.pathname}`;
        return;
      }

      const commentsPathMatch = matchPath(location.pathname, {
        path: '/contents/:id/comments',
      });
      if (commentsPathMatch) {
        location.pathname = `/webview${location.pathname}`;
        return;
      }

      const contentDetailMatch = matchPath(location.pathname, {
        path: '/contents/:id',
        exact: true,
      });
      if (contentDetailMatch) {
        location.pathname = `/webview${location.pathname}/comments`;
        return;
      }

      const deeplinkUrl = getDeeplinkUrl(location.pathname, programId, orgSlug);
      if (deeplinkUrl) {
        window.location.href = deeplinkUrl;
      } else {
        window.location.href = EXIT_DEEPLINK;
      }

      //you might think calling 'unblock()` here would be the solution, but the mobile app requires a full stop in navigation to work properly.
      //until we find a better way, we have to be satified with this hack.
      throw 'intentional halt';
    });

    return unblock;
  }, [programId, orgSlug]);

  const contentLink = usePrependProgramPath(`/contents/${contentId}`);

  const newContentPage = useSelector((state: RootPatronState) =>
    getFeatureFlag(state, Feature.CONTENT_DETAIL_NEW)
  );

  return (
    <>
      {fetching ? (
        <Spinner />
      ) : (
        <>
          {newContentPage ? (
            <CommentsV2Wrapper
              contentId={contentId}
              description={content?.description}
              link={contentLink}
              title={content?.title}
            />
          ) : (
            <CommentsV1Wrapper
              contentId={contentId}
              description={content?.description}
              link={contentLink}
              title={content?.title}
            />
          )}
        </>
      )}
    </>
  );
};

// Exported for test: this block is where FE-38038's mention rendering lives.
export const CommentsContextBlock = ({
  title,
  description,
  link,
}: {
  title?: string;
  description?: string;
  link: string;
}) => {
  const { t } = useTranslation();

  // FE-38038: title/description hold `@{ref:user::id:N::name:Name}` tokens now that this
  // screen requests `with_metadata` above, so they have to go through TextWithMentions —
  // interpolating them raw printed the token verbatim to the user, which is worse than the
  // `@Name` this surface showed before the format was requested.
  //
  // The whole block used to be one wrapping <Link>, which cannot contain a mention anchor:
  // nested <a> is invalid HTML. So the navigation link is now an absolutely positioned
  // overlay and the mention anchors are siblings lifted above it — the same structure
  // @socialchorus/shared-ui-components' FeedCard uses for exactly this reason. The overlay
  // keeps the whole block tappable (what the native apps embedding this webview expect)
  // while leaving the mentions independently clickable.
  return (
    <div className="comment-context-block">
      <Link
        className="comment-context-block__overlay"
        href={link}
        aria-label={flattenMentions(title)}
      />
      <div className="comment-context">
        <div className="comment-context__title">
          {t('comments.in_response')}
        </div>

        <div className="comment-context__body">
          <h5>
            {' '}
            <TextWithMentions text={title ?? ''} />{' '}
          </h5>
          <p>
            {' '}
            <TextWithMentions text={description ?? ''} />{' '}
          </p>
        </div>
      </div>
    </div>
  );
};

type CommentsWrapperProps = {
  contentId: Content['id'];
  title?: Content['title'];
  description?: Content['description'];
  link: string;
};

const CommentsV2Wrapper = ({
  contentId,
  title,
  description,
  link,
}: CommentsWrapperProps) => {
  cx;
  return (
    <div className="responsive-content-page comments-web-view-v2">
      <CommentsContextBlock
        description={description}
        title={title}
        link={link}
      />
      <ContentComments contentId={contentId} />
    </div>
  );
};

const CommentsV1Wrapper = ({
  contentId,
  title,
  description,
  link,
}: CommentsWrapperProps) => {
  return (
    <div className="heartsplosion-container comments-web-view">
      <CommentsContextBlock
        description={description}
        title={title}
        link={link}
      />
      <ContentInfoComments contentId={contentId} />
    </div>
  );
};

export default CommentsWebView;
